import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationStart, Params, Router } from '@angular/router';
import { FeatureToggleService } from '@app/modules/feature-toggles/services/feature-toggle.service';
import { LocationFacade } from '@app/modules/location/facade/location.facade';
import { PlatformFacade } from '@app/modules/platform/facade/platform.facade';
import { DataDogService } from '@app/services/data-dog.service';
import { LoadingAnimationService } from '@app/services/loading-animation.service';
import { MobileLayoutService } from '@app/services/mobile-layout.service';
import { ServerOfflineService } from '@app/services/server-offline.service';
import { SidenavConfigService } from '@app/services/sidenav-config.service';
import { EntityResource, EntityType } from '@app/store/filters/models/filters.model';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@environments/environment';
import { PendoService, ZonarUiAnalyticsService } from '@app/modules/analytics';
import { PermissionsService } from '@app/modules/auth';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { DateFormatService, DateFormatType } from './services/date-format/date-format.service';
import { SettingsFacade } from './modules/settings/facade/settings.facade';
import { Company } from './modules/auth/models/core-company-api.model';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from './modules/shared/components/generic-dialog/generic-dialog.component';
import { SettingsApiService } from './services/settings-api.service';
import { MapSettings } from './modules/location/models/settings.model';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private auth: AuthService,
    private datadog: DataDogService,
    private locationFacade: LocationFacade,
    private mobileLayoutService: MobileLayoutService,
    private serverOfflineService: ServerOfflineService,
    private overlayContainer: OverlayContainer,
    private pendoService: PendoService,
    private permissionsService: PermissionsService,
    private platformFacade: PlatformFacade,
    private router: Router,
    private zuiAnalytics: ZonarUiAnalyticsService,
    public loadingAnimationService: LoadingAnimationService,
    private featureToggleService: FeatureToggleService,
    private sidenavConfig: SidenavConfigService,
    private activatedRoute: ActivatedRoute,
    private settingsFacade: SettingsFacade,
    private dateFormatService: DateFormatService,
    private readonly settingsService: SettingsApiService,
    private route: ActivatedRoute,
    public dialog: MatDialog
  ) {}
  @ViewChild('sidenav', { read: ElementRef }) sidenav: ElementRef;
  isMobile$ = this.platformFacade.getIsMobile();
  currentCompany: EntityResource;
  loading = ResourceLoadState.LOADING;
  currentLoadState = ResourceLoadState.INITIAL;
  newLoadState: Subscription;
  hamburgerMenuEnabled: boolean = environment.hamburgerMenuEnabled;
  sidenavParams = this.sidenavConfig.getSidenavParams();
  sidenavHeader = this.sidenavConfig.getSidenavHeaderConfig();
  sidenavMenu = null; // ZTT-3116 if we can rip out EU menu, this can be deleted
  sidenavFooter = null;
  onDestroy$ = new Subject<void>();
  pageTitleEl: HTMLElement;
  hideMobileMenuButton = false;
  userEmailOnInit: string = '';
  demoMode: string = null;
  accountId: string;
  private visitStart = false;
  public windowComponent: Window = window;
  private isMobile: boolean = false;

  // TODO: Do we still want this after VDO app is separated?
  logoutIfConditionsMet = (event, auth) => {
    // when we hide the sidenav logout button, provide a /logout route so that users can still logout
    if (event instanceof NavigationStart && this.sidenavParams.hideLogoutButton && event.url.startsWith('/logout')) {
      auth.logout();
    }
  };

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const vdoAccountParam = urlParams.get('vdoAccount');
    this.accountId = vdoAccountParam ?? null;
    //if the only param is vdoAccount it means we've just entered from vdofleet
    this.visitStart = vdoAccountParam != null && Array.from(urlParams.keys()).length === 1;
    this.demoMode = sessionStorage.getItem('demoMode') ?? null;
    this.init();

    this.settingsFacade.getDisplaySettings().subscribe(settings => {
      const dateFormat: DateFormatType = settings.dateFormat === 'dd/MM/yyyy' ? 'DDMMYYYY' : 'MMDDYYYY';
      this.dateFormatService.setFormat(dateFormat);
    });
    this.route.queryParams.subscribe(params => {
      if (params.demoMode?.toLowerCase() === 'false') {
        sessionStorage.removeItem('demoMode');
        sessionStorage.removeItem('routeActivation');
        sessionStorage.removeItem('assetVINsMap');
      }
    });
    this.validateAccountId();
  }

  init() {
    if (this.visitStart) {
      sessionStorage.removeItem('demoMode');
      sessionStorage.removeItem('routeActivation');
      sessionStorage.removeItem('assetVINsMap');
      this.visitStart = false;
    }
    this.pageTitleEl = document.getElementById('maps-page-title');
    // Set page title
    if (this.pageTitleEl) {
      this.pageTitleEl.innerText = 'VDO Fleet';
    }

    this.sidenavFooter = this.sidenavConfig.getSidenavFooterConfig();
    // ZTT-3116 if EU left in uncomment, otherwise delete
    this.sidenavConfig
      .getSidenavMenuConfig$()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(menuConfig => {
        this.sidenavMenu = menuConfig;
      });

    this.serverOfflineService.showNotificationForOfflineStatus();
    this.serverOfflineService.refreshAppForOnlineStatus();

    if (!environment.devTools) {
      this.auth.isAuthenticated$.pipe(filter(isAuthenticated => Boolean(isAuthenticated))).subscribe(() => {
        // Start DataDog session replay recording
        this.datadog.startSessionReplayRecording();
      });
    }
    this.mobileLayoutService.compensate();
    this.mobileLayoutService.addCompensationListener();
    this.datadog.addRumTiming('app_init');
    this.getCompaniesLoadState();

    // when we initialize pendo, some of the api calls require a zonar owner id, which can only be attached to the
    // request after the permissions are loaded. therefore, we must wait to initialize pendo until after the
    // permissions are loaded. ultimately, this logic should be handled in the pendo service itself as described here:
    // https://jira.zonarsystems.net/browse/ZTT-1015
    this.permissionsService.permissions$.pipe(filter(permissions => Boolean(permissions))).subscribe(() => {
      this.pendoService.initialize();
    });

    this.permissionsService
      .getCurrentCompany()
      .pipe(filter((company: Company) => !!company?.id))
      .subscribe((company: Company) => {
        this.setCurrentCompany({ title: company.name, value: company.id });
      });

    this.router.events.subscribe(event => {
      this.logoutIfConditionsMet.call(this, event, this.auth);
    });

    // show/hide hamburger menu based on VDO native app CNAME
    if (window.location.hostname.includes('vdo-native-app')) {
      this.hideMobileMenuButton = true;
    }

    document.body.classList.remove('zonar-default-theme');
    document.body.classList.add('zonar-vdo-theme');

    this.isMobile$.pipe(filter(isMobile => Boolean(isMobile))).subscribe(() => {
      this.isMobile = true;
      this.overlayContainer.getContainerElement().classList.add('app-container-mobile');
    });
  }

  getCompaniesLoadState() {
    this.newLoadState = this.locationFacade.getCompaniesLoadState().subscribe(loadState => {
      if (this.currentLoadState === ResourceLoadState.INITIAL && loadState === ResourceLoadState.LOADING) {
        this.datadog.addRumTiming('loading_spinner_visible');
      }
      if (this.currentLoadState === ResourceLoadState.LOADING && loadState === ResourceLoadState.LOAD_SUCCESSFUL) {
        this.datadog.addRumTiming('loading_spinner_no_longer_visible');

        // solves an edge case in mobile zonar user experience where main spinner is briefly seen if user expands asset details right away
        this.loadingAnimationService.shouldShowMainAppSpinner = false;
      }
      if (this.currentLoadState === ResourceLoadState.INITIAL && loadState === ResourceLoadState.LOAD_FAILURE) {
        this.loadingAnimationService.shouldShowMainAppSpinner = false;
      }
      this.currentLoadState = loadState;
    });
  }

  ngOnDestroy() {
    // Stop DataDog session replay recording
    this.datadog.stopSessionReplayRecording();
    this.newLoadState.unsubscribe();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  setCurrentCompanyToState(company: EntityResource) {
    // this.locationFacade.clearFilters();
    if (company?.id) {
      this.locationFacade.getDivisionsByCompanyId(company.id);
      // load vdo data for account only once when company is set
      this.locationFacade.loadVehicles(company.id);
      // setTimeout(() => this.locationFacade.loadAccountDrivers(company.id), 4000)
      this.locationFacade.loadAccountDrivers(company.id);

      // wait for TM vehicles to be loaded, we need vins before fetching assets list from live api
      // setCompanyFilter will trigger assets load from vins
      this.locationFacade
        .getVehiclesLoadState()
        .pipe(
          filter(state => state != ResourceLoadState.LOADING),
          take(1)
        )
        .subscribe((state: ResourceLoadState) => {
          if (state == ResourceLoadState.LOAD_SUCCESSFUL) {
            this.locationFacade.getAllVehicles().subscribe(vehiclesMap => {
              this.locationFacade.setCurrentCompany(company);
              this.locationFacade.setCompanyFilter(company.id);

              if (vehiclesMap.size > 0) {
                combineLatest([this.locationFacade.getAllDriversLoadState(), this.locationFacade.getAssetsLoadState()])
                  .pipe(
                    filter(([driverLoadState, assetsLoadState]) => {
                      return (
                        driverLoadState === ResourceLoadState.LOAD_SUCCESSFUL &&
                        assetsLoadState === ResourceLoadState.LOAD_SUCCESSFUL
                      );
                    }),
                    take(1)
                  )
                  .subscribe(() => {
                    this.locationFacade.forceUpdateAssets();
                    this.applySavedSortFilters();
                  });
              }
            });
          } else if (state == ResourceLoadState.LOAD_FAILURE) {
            this.locationFacade.setAssetsError();
          }
        });
    }

    // this.activatedRoute.queryParams.subscribe((params: Params) => {
    //   if (params.demoMode != undefined) {
    //     this.handleCompaniesAnonymizedData(params.demoMode === 'true');
    //   }
    // });
  }

  applySavedSortFilters() {
    this.settingsService.getSetting(MapSettings.MAP_SORT_ORDER).subscribe(response => {
      if (response?.value !== null || response?.value !== undefined) {
        const { sortKey, sortAsc } = this.mapSettingToSortParams(response.value) || {};
        if (sortKey !== undefined && sortAsc !== undefined) {
          const sorting = {
            sortOrder: sortAsc ? 'asc' : 'desc',
            sortAttribute: sortKey
          };
          this.locationFacade.applySorting(sorting);
        }
      }
    });
  }

  mapSettingToSortParams(setting) {
    switch (setting) {
      case 'ASSET_ASC':
        return { sortKey: 'assetName', sortAsc: true };
      case 'ASSET_DESC':
        return { sortKey: 'assetName', sortAsc: false };
      case 'DRIVER_ASC':
        return { sortKey: 'driverName', sortAsc: true };
      case 'DRIVER_DESC':
        return { sortKey: 'driverName', sortAsc: false };
      case 'OLDEST':
        return { sortKey: 'geoEventTs', sortAsc: true };
      case 'NEWEST':
        return { sortKey: 'geoEventTs', sortAsc: false };
      default:
        return null;
    }
  }
  setCurrentCompany(company: { title: string; value: string }) {
    // this if stops unneeded asset refresh if company has not changed on new emit (ie from sidenav being re-expanded)
    if (company?.value && company.value != this.currentCompany?.id) {
      this.currentCompany = {
        name: company?.title,
        id: company?.value,
        type: EntityType.COMPANY,
        anonymizedData: company?.title.includes('DEMO')
      };
      // Need the next line or things using devcycle like Layers control and locator tool will not show up
      this.featureToggleService.initializeDevCycle(this.currentCompany.id);
      this.setCurrentCompanyToState(this.currentCompany);
    }
    if (!company) {
      this.locationFacade.setAssetsError();
    }
  }

  onSideNavMobileMenuButtonToggled(event) {
    this.sidenavParams.mobileOpened = event;
  }

  validateAccountId() {
    this.permissionsService.getUser().subscribe(user => {
      const userAccountId = +user?.account_id;
      if (!userAccountId) {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
          data: {
            title: 'error.title',
            content: 'error.invalidAccount',
            confirmButtonText: 'appSelectedAssetNavigation.close',
            note: 'error.vdoRedirect'
          }
        });
        dialogRef.afterClosed().subscribe(() => {
          const vdoLink = environment.euUrl?.vdoDashboardUrl ? environment.euUrl.vdoDashboardUrl : '';
          const path = this.isMobile ? `${environment.appUrl}` : vdoLink;
          this.navigateTo(path);
        });
      }
    });
  }
  navigateTo(path: string) {
    const externalLinkPrefix = 'http';
    /* external link */
    if (path.toLocaleLowerCase().startsWith(externalLinkPrefix)) {
      this.windowComponent.location.href = path;
    } else {
      /* internal link */
      this.router.navigate([path]);
    }
  }
}
